<template>
    <section :data-mui-theme="theme" class="slice slice-numbers slice-numbers--default" aria-label="numbers" data-testid="numbers-default-slice">
        <div class="slice-content">
            <div class="slice-numbers--default__layout mui-background-lighter" :class="{'slice-numbers--default__layout--four-pills': hasFourPills}">
                <div class="slice-numbers--default__header">
                    <PrismicRichText v-if="isFilled.richText(slice.primary.title)" :field="slice.primary.title" class="mui-title" />
                    <p
                        v-if="isFilled.keyText(slice.primary.description)"
                        class="slice-numbers--default__description"
                        data-testid="numbers-default-description"
                    >
                        {{ slice.primary.description }}
                    </p>
                </div>

                <ul
                    v-if="isFilled.group(slice.primary.numbers)"
                    class="slice-numbers--default__stats mui-background"
                    data-testid="numbers-default-stats"
                >
                    <li
                        v-for="(item, index) in slice.primary.numbers"
                        :key="index"
                        :class="['slice-numbers--default__stat', {'slice-numbers--default__stat--highlighted': activeIndex === index}]"
                        :data-testid="`numbers-default-stat-${index}`"
                    >
                        <span v-if="isFilled.keyText(item.keyNumber)" class="slice-numbers--default__stat-value">
                            {{ item.keyNumber }}
                        </span>
                        <span v-if="isFilled.keyText(item.description)" ref="statDescription" class="slice-numbers--default__stat-description">
                            {{ item.description }}
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {onMounted, onUnmounted, ref, computed, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';

    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {NumbersSlice} from '@prismicio-types';
    import {useDisplayMobile} from '@malt/utils';

    type NumbersDefaultInZone = Extract<NumbersSlice, {variation: 'default'}>;

    const HIGHLIGHT_INTERVAL_MS = 3000;

    const props = defineProps(getSliceComponentProps<NumbersDefaultInZone>());

    const theme = computed(() => props.slice.primary.theme);
    const numbersCount = computed(() => props.slice.primary.numbers?.length ?? 0);
    const hasFourPills = computed(() => numbersCount.value === 4);

    const activeIndex = ref(0);
    const highlightInterval = ref<ReturnType<typeof setInterval>>();
    const statDescription = useTemplateRef<HTMLElement[]>('statDescription');

    const {isDisplayMobile} = useDisplayMobile('Sm');

    function getStatDescriptions(): HTMLElement[] {
        const descriptions = statDescription.value;
        if (!descriptions) {
            return [];
        }
        return Array.isArray(descriptions) ? descriptions : [descriptions];
    }

    function clearDescriptionHeights() {
        getStatDescriptions().forEach((description) => {
            description.style.minHeight = '';
        });
    }

    function syncDescriptionHeights() {
        if (!isDisplayMobile.value) {
            return;
        }

        clearDescriptionHeights();
        const descriptions = getStatDescriptions();

        const maxHeight = Math.max(...descriptions.map((description) => description.offsetHeight));
        descriptions.forEach((description) => {
            description.style.minHeight = `${maxHeight}px`;
        });
    }

    onMounted(() => {
        syncDescriptionHeights();
        window.addEventListener('resize', syncDescriptionHeights);

        if (numbersCount.value <= 1) {
            return;
        }

        highlightInterval.value = setInterval(() => {
            activeIndex.value = (activeIndex.value + 1) % numbersCount.value;
        }, HIGHLIGHT_INTERVAL_MS);
    });

    onUnmounted(() => {
        if (highlightInterval.value) {
            clearInterval(highlightInterval.value);
        }
        window.removeEventListener('resize', syncDescriptionHeights);
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-numbers {
        &--default {
            .slice-content {
                text-align: left;
            }

            &__layout {
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-8);
                border-radius: var(--joy-core-spacing-6);
                padding: var(--joy-core-spacing-9) var(--joy-core-spacing-6);

                @include mq.screen_md(min) {
                    flex-direction: row;
                    align-items: center;
                    gap: var(--joy-core-spacing-9);
                }

                @include mq.screen_xlg(min) {
                    padding: 145px var(--joy-core-spacing-8);
                }

                &--four-pills {
                    @include mq.screen_md(min) {
                        flex-direction: column;
                        align-items: stretch;
                        gap: var(--joy-core-spacing-8);
                    }

                    @include mq.screen_xlg(min) {
                        flex-direction: row;
                        align-items: center;
                        gap: var(--joy-core-spacing-9);
                    }
                }
            }

            &__header {
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-3);
            }

            &__description {
                @include text.getFontBySize(primary-600);
                color: var(--joy-color-neutral-70);
                line-height: var(--joy-line-height-large);
                font-weight: var(--joy-font-weight-normal);
                letter-spacing: var(--joy-font-letter-spacing-tight);
            }

            &__stats {
                display: flex;
                flex-wrap: wrap;
                gap: var(--joy-core-spacing-3);
                padding: var(--joy-core-spacing-8);
                border-radius: var(--joy-core-spacing-5);
                list-style: none;
                margin: 0 auto;

                @include mq.screen_lg(min) {
                    flex-wrap: nowrap;
                    border-radius: 150px;
                }
            }

            &__stat {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                width: 100%;
                padding: var(--joy-core-spacing-5);
                border-radius: 90px;
                background-color: rgba(255, 255, 255, 0.5);
                color: var(--joy-color-primary-90);
                border: 1px solid rgba(166, 54, 51, 0.15);
                transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);

                .slice-numbers--default[data-mui-theme='secondary'] & {
                    color: var(--joy-color-brand-primary-70);
                    background-color: var(--joy-color-background-default);
                }

                @include mq.screen_md(min) {
                    min-width: 220px;
                }

                &--highlighted {
                    background-color: var(--joy-color-primary-90);
                    color: var(--joy-color-neutral-0);
                    transform: scale(1.05);

                    .slice-numbers--default[data-mui-theme='secondary'] & {
                        background-color: var(--joy-color-brand-primary-70);
                        color: var(--joy-color-neutral-0);
                    }
                }
            }

            &__stat-value {
                @include text.getFontBySize(secondary-600);
                font-weight: var(--joy-font-weight-bold);
            }

            &__stat-description {
                @include text.getFontBySize(primary-400);
                letter-spacing: var(--joy-font-letter-spacing-tight);
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
            }
        }
    }
</style>
